import { supabase } from "./supabase";
import type { Faixa } from "./simplesNacional";

// O IO do Radar Simples (000184-000186): o catálogo, a busca de CNAE, as
// aberturas (os clientes do aluno), os itens dos checklists e das planilhas, e o
// histórico de análises.
//
// ---------------------------------------------------------------------------
// Zero regra aqui
// ---------------------------------------------------------------------------
// Quem decide se o aluno pode ler ou gravar é o banco — todas as funções `rs_*`
// perguntam a `abrir_card`, o mesmo portão da vitrine e da ponte `/abrir`. Este
// arquivo lê, grava e tipa. As regras de cálculo moram em `simplesNacional.ts`,
// `elegibilidadeSimples.ts`, `custoFuncionarioSimples.ts` e `reformaSimples.ts`,
// que são puros e têm prova própria.
//
// ---------------------------------------------------------------------------
// NENHUM `select` cru — nem para o que "parece público"
// ---------------------------------------------------------------------------
// As sete tabelas `rs_*` têm `revoke all` ou policy de dono, e a leitura sai por
// função. É a lição da 000136: lá a tela do aluno fazia `select` numa tabela
// cuja policy tem `or eh_suporte()`, e a conta de admin recebeu os 6.338
// emitidos de todo mundo — 141.111 pixels de altura numa aba.
//
// Na origem o defeito era maior: `Historico.tsx` fazia `select * from analises`
// sem cláusula de aluno, sobre uma policy `using (true)`. Todo aluno via as 86
// análises de 55 colegas.

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type Cnae = {
  codigo: string;
  num_cnae: string;
  descricao: string;
  tipo: "Comércio" | "Indústria" | "Serviço";
  permitido_simples: "Sim" | "Não" | "Revisar";
  anexo_principal: number | null;
  exige_fator_r: boolean;
  /** O que a atividade É. Na origem chamava-se `vedacao` e virava alerta laranja. */
  classificacao: string | null;
  /** A parte que de fato pede atenção ("validar enquadramento específico"). */
  ressalva: string | null;
  nota_fpas_rat: string | null;
  base_legal: string | null;
  fpas: string | null;
  rat: number | null;
  grau_risco: number | null;
  fpas_rat_criterio: string | null;
};

export type BlocoDeItens =
  | "obrigacoes"
  | "entrega"
  | "custo-abertura"
  | "custo-mensal"
  | "honorarios";

export type Item = {
  id: string;
  bloco: BlocoDeItens;
  secao: string;
  secao_ordem: number;
  ordem: number;
  texto: string;
  qualificador: string | null;
  valor_sugerido: number | null;
  ligado_por_padrao: boolean;
  aviso_da_secao: string | null;
};

export type Parametro = {
  valor: number;
  rotulo: string;
  /** De onde o número veio — vai para a TELA junto do valor. */
  referencia: string;
  unidade: "percentual" | "reais";
};

export type Catalogo = {
  faixas: Faixa[];
  itens: Item[];
  parametros: Record<string, Parametro>;
};

export type Abertura = {
  id: string;
  token: string;
  rotulo: string | null;
  status: "pendente" | "respondido";
  criado_em: string;
  respondido_em: string | null;
  respostas: number;
  itens_marcados: number;
};

export type RespostaDoCliente = {
  id: string;
  criado_em: string;
  respostas: Record<string, unknown>;
};

export type ItemDaAbertura = {
  item_id: string;
  marcado: boolean;
  quantidade: number;
  valor_unitario: number;
};

export type AberturaCompleta = {
  abertura: Omit<Abertura, "respostas" | "itens_marcados">;
  respostas: RespostaDoCliente[];
  itens: ItemDaAbertura[];
};

export type Analise = {
  id: string;
  seq: number;
  modulo: "fator-r" | "aliquota" | "reforma";
  cnae_codigo: string | null;
  cnae_descricao: string | null;
  anexo_aplicavel: number | null;
  receita_bruta_12m: number | null;
  folha_12m: number | null;
  fator_r: number | null;
  aliquota_efetiva: number | null;
  das_mensal: number | null;
  detalhe: Record<string, unknown> | null;
  criado_em: string;
};

export type Leitura<T> =
  | { fase: "pronto"; dados: T }
  | { fase: "recusado"; motivo: string }
  | { fase: "erro" };

/** O resultado de uma escrita: ou deu certo, ou tem um motivo NOMEADO. */
export type Escrita<T = void> =
  | ({ ok: true } & (T extends void ? Record<string, never> : T))
  | { ok: false; motivo: string; mensagem?: string };

// ---------------------------------------------------------------------------
// As mensagens de cada recusa
// ---------------------------------------------------------------------------
//
// Cada motivo tem texto próprio porque as AÇÕES são diferentes: assinar o
// contrato é coisa que o aluno resolve na hora; "estamos preparando" é esperar.
// Uma mensagem só para todos ensina o aluno a ignorar a mensagem.
const MENSAGEM: Record<string, string> = {
  "sem-acesso": "Você não tem acesso vigente à Pós em Rotinas do Simples Nacional.",
  "contrato-pendente":
    "Assine o contrato do curso para liberar as ferramentas. O botão está na página do curso.",
  "em-breve": "Esta ferramenta ainda está sendo preparada.",
  "card-nao-encontrado": "Esta ferramenta não está no seu catálogo.",
  "abertura-nao-encontrada": "Este cliente não foi encontrado na sua lista.",
  "abertura-respondida":
    "Este cliente já respondeu ao questionário. As respostas dele ficam guardadas.",
  "item-nao-encontrado": "Este item saiu da lista. Recarregue a página.",
  "limite-de-aberturas": "Você chegou ao limite de clientes. Fale com o suporte.",
  "modulo-desconhecido": "Não foi possível guardar esta simulação.",
  "analise-nao-encontrada": "Esta simulação não está mais no seu histórico.",
};

export function mensagemDaRecusa(motivo: string, doServidor?: string): string {
  // A mensagem do SERVIDOR vence quando existe: ela é específica (traz o limite,
  // o número, o estado), e a daqui é o texto genérico do motivo.
  return doServidor ?? MENSAGEM[motivo] ?? "Não foi possível concluir. Tente de novo.";
}

// ---------------------------------------------------------------------------
// Leitura
// ---------------------------------------------------------------------------

type RespostaRpc = { ok?: boolean; motivo?: string; mensagem?: string; [k: string]: unknown };

function interpretar<T>(data: unknown, error: unknown, montar: (r: RespostaRpc) => T): Leitura<T> {
  if (error) return { fase: "erro" };
  const r = (data ?? {}) as RespostaRpc;
  if (r.ok !== true) return { fase: "recusado", motivo: r.motivo ?? "sem-acesso" };
  return { fase: "pronto", dados: montar(r) };
}

export async function lerCatalogo(curso: string): Promise<Leitura<Catalogo>> {
  const { data, error } = await supabase.rpc("rs_catalogo", { p_curso: curso });
  return interpretar(data, error, (r) => ({
    faixas: (r.faixas ?? []) as Faixa[],
    itens: (r.itens ?? []) as Item[],
    parametros: (r.parametros ?? {}) as Record<string, Parametro>,
  }));
}

export type ResultadoBusca = {
  termo: string;
  /** Quantos casaram no total — pode ser maior que `cnaes.length`. */
  total: number;
  teto: number;
  cnaes: Cnae[];
};

/**
 * A busca de CNAE.
 *
 * O `total` vem junto do lote de propósito: lista cortada sem o total vira "não
 * existe" na cabeça de quem procura. A tela mostra "50 de 213".
 */
export async function buscarCnae(
  curso: string,
  termo: string,
  limite = 50,
): Promise<Leitura<ResultadoBusca>> {
  const { data, error } = await supabase.rpc("rs_buscar_cnae", {
    p_curso: curso,
    p_termo: termo,
    p_limite: limite,
  });
  return interpretar(data, error, (r) => ({
    termo: String(r.termo ?? ""),
    total: Number(r.total ?? 0),
    teto: Number(r.teto ?? limite),
    cnaes: (r.cnaes ?? []) as Cnae[],
  }));
}

export async function lerAberturas(curso: string): Promise<Leitura<Abertura[]>> {
  const { data, error } = await supabase.rpc("rs_minhas_aberturas", { p_curso: curso });
  return interpretar(data, error, (r) => (r.aberturas ?? []) as Abertura[]);
}

export async function lerAbertura(curso: string, id: string): Promise<Leitura<AberturaCompleta>> {
  const { data, error } = await supabase.rpc("rs_abertura", { p_curso: curso, p_abertura: id });
  return interpretar(data, error, (r) => ({
    abertura: r.abertura as AberturaCompleta["abertura"],
    respostas: (r.respostas ?? []) as RespostaDoCliente[],
    itens: (r.itens ?? []) as ItemDaAbertura[],
  }));
}

export async function lerHistorico(curso: string, limite = 100): Promise<Leitura<Analise[]>> {
  const { data, error } = await supabase.rpc("rs_meu_historico", {
    p_curso: curso,
    p_limite: limite,
  });
  return interpretar(data, error, (r) => (r.analises ?? []) as Analise[]);
}

// ---------------------------------------------------------------------------
// Escrita
// ---------------------------------------------------------------------------

function comoEscrita<T extends object>(data: unknown, error: unknown): Escrita<T> {
  if (error) return { ok: false, motivo: "erro-de-rede" };
  const r = (data ?? {}) as RespostaRpc;
  if (r.ok !== true) {
    return { ok: false, motivo: r.motivo ?? "erro", mensagem: r.mensagem as string | undefined };
  }
  return { ...(r as object), ok: true } as Escrita<T>;
}

export async function criarAbertura(
  curso: string,
  rotulo: string,
): Promise<Escrita<{ id: string; token: string }>> {
  const { data, error } = await supabase.rpc("rs_criar_abertura", {
    p_curso: curso,
    p_rotulo: rotulo,
  });
  return comoEscrita<{ id: string; token: string }>(data, error);
}

export async function renomearAbertura(
  curso: string,
  id: string,
  rotulo: string,
): Promise<Escrita> {
  const { data, error } = await supabase.rpc("rs_renomear_abertura", {
    p_curso: curso,
    p_abertura: id,
    p_rotulo: rotulo,
  });
  return comoEscrita(data, error);
}

export async function apagarAbertura(curso: string, id: string): Promise<Escrita> {
  const { data, error } = await supabase.rpc("rs_apagar_abertura", {
    p_curso: curso,
    p_abertura: id,
  });
  return comoEscrita(data, error);
}

export async function gravarItem(
  curso: string,
  abertura: string,
  item: string,
  marcado: boolean,
  quantidade?: number,
  valor?: number,
): Promise<Escrita> {
  const { data, error } = await supabase.rpc("rs_gravar_item", {
    p_curso: curso,
    p_abertura: abertura,
    p_item: item,
    p_marcado: marcado,
    p_quantidade: quantidade ?? null,
    p_valor: valor ?? null,
  });
  return comoEscrita(data, error);
}

export type AnaliseParaSalvar = {
  modulo: "fator-r" | "aliquota" | "reforma";
  detalhe: Record<string, unknown>;
  cnae?: string | null;
  anexo?: number | null;
  receita?: number | null;
  folha?: number | null;
  fatorR?: number | null;
  aliquota?: number | null;
  das?: number | null;
};

export async function salvarAnalise(
  curso: string,
  a: AnaliseParaSalvar,
): Promise<Escrita<{ id: string }>> {
  const { data, error } = await supabase.rpc("rs_salvar_analise", {
    p_curso: curso,
    p_modulo: a.modulo,
    p_detalhe: a.detalhe,
    p_cnae: a.cnae ?? null,
    p_anexo: a.anexo ?? null,
    p_receita: a.receita ?? null,
    p_folha: a.folha ?? null,
    p_fator_r: a.fatorR ?? null,
    p_aliquota: a.aliquota ?? null,
    p_das: a.das ?? null,
  });
  return comoEscrita<{ id: string }>(data, error);
}

export async function apagarAnalise(curso: string, id: string): Promise<Escrita> {
  const { data, error } = await supabase.rpc("rs_apagar_analise", {
    p_curso: curso,
    p_analise: id,
  });
  return comoEscrita(data, error);
}

// ---------------------------------------------------------------------------
// O formulário público — o cliente do aluno, sem conta
// ---------------------------------------------------------------------------
//
// As duas únicas funções do Radar que `anon` alcança. Quem abre não tem conta na
// CF e não tem suporte para acionar: nenhuma mensagem técnica chega aqui.

export type EstadoDoFormulario =
  | { estado: "pendente"; rotulo: string | null }
  | { estado: "respondido" }
  | { estado: "nao-encontrado" }
  | { estado: "erro" };

export async function lerEstadoDoFormulario(token: string): Promise<EstadoDoFormulario> {
  const { data, error } = await supabase.rpc("rs_formulario_estado", { p_token: token });
  if (error) return { estado: "erro" };
  const r = (data ?? {}) as { estado?: string; rotulo?: string | null };
  if (r.estado === "pendente") return { estado: "pendente", rotulo: r.rotulo ?? null };
  if (r.estado === "respondido") return { estado: "respondido" };
  return { estado: "nao-encontrado" };
}

export async function enviarFormulario(
  token: string,
  respostas: Record<string, unknown>,
): Promise<Escrita> {
  const { data, error } = await supabase.rpc("rs_formulario_enviar", {
    p_token: token,
    p_respostas: respostas,
  });
  return comoEscrita(data, error);
}
